import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import Header from '../components/Header';

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<number[]>([]);

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How do I find my school on BandCam.io?",
          answer: "Simply visit our schools page and search for your school by name or location. If your school isn't listed, you can request to add it by contacting our support team."
        },
        {
          question: "What occasions can I order a video for?",
          answer: "We offer videos for birthdays, graduations, anniversaries, congratulations, get well soon messages, and other special celebrations. You can specify your occasion when placing your order."
        },
        {
          question: "How long does it take to receive my video?",
          answer: "Most videos are delivered within 5-7 business days after your order is confirmed. Rush delivery options are available for an additional fee."
        },
        {
          question: "Can I request specific songs or music?",
          answer: "Yes! You can request specific songs or musical styles when placing your order. Our bands will do their best to accommodate your preferences, subject to copyright and licensing restrictions."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      questions: [
        {
          question: "How much does a personalized band video cost?",
          answer: "Our standard videos start at $49.99. Pricing may vary based on video length, complexity, and any special requests. All pricing is clearly displayed before you place your order."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards (Visa, MasterCard, American Express, Discover), PayPal, and Apple Pay. All payments are processed securely through our payment partners."
        },
        {
          question: "What's your refund policy?",
          answer: "We offer a 100% satisfaction guarantee. If you're not completely satisfied with your video, we'll work with you to make it right or provide a full refund within 30 days of delivery."
        },
        {
          question: "Do you offer any discounts?",
          answer: "Yes! We offer discounts for bulk orders, returning customers, and special promotions throughout the year. Sign up for our newsletter to stay updated on the latest offers."
        }
      ]
    },
    {
      category: "Video Delivery",
      questions: [
        {
          question: "How will I receive my video?",
          answer: "Your video will be delivered via email with a secure download link. You can also access your videos through your account dashboard on our website."
        },
        {
          question: "What video quality can I expect?",
          answer: "All our videos are recorded in high definition (1080p) and delivered in MP4 format. The quality is optimized for both viewing on devices and sharing on social media."
        },
        {
          question: "Can I share my video on social media?",
          answer: "Absolutely! Your video is yours to keep and share. You can post it on social media, send it to friends and family, or use it however you'd like."
        },
        {
          question: "What if there's an issue with my video?",
          answer: "If you experience any issues with your video, please contact our support team immediately. We'll work quickly to resolve any problems and ensure you're completely satisfied."
        }
      ]
    },
    {
      category: "School Partnerships",
      questions: [
        {
          question: "How can my school become a partner?",
          answer: "We're always looking for new school partners! Contact our partnerships team at partnerships@bandcam.io to learn more about the requirements and benefits of partnering with us."
        },
        {
          question: "What equipment do schools need?",
          answer: "Schools need basic video recording equipment (smartphone or camera), good lighting, and a quiet space for recording. We provide detailed guidelines and support to ensure quality results."
        },
        {
          question: "How do schools benefit from partnering?",
          answer: "Partner schools receive a percentage of revenue from each video order, helping to fund their music programs. They also gain exposure and strengthen community connections."
        },
        {
          question: "What support do you provide to schools?",
          answer: "We provide comprehensive support including training materials, technical assistance, marketing support, and ongoing guidance to help schools succeed with our platform."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "I can't download my video. What should I do?",
          answer: "First, check your email spam folder. If you still can't find the download link, contact our support team and we'll send you a new link immediately."
        },
        {
          question: "The video won't play on my device. Help!",
          answer: "Our videos are compatible with most devices and media players. If you're having trouble, try using VLC Media Player or contact our support team for assistance."
        },
        {
          question: "Can I request changes to my video after it's delivered?",
          answer: "We want you to be completely satisfied! If you'd like changes to your video, contact us within 7 days of delivery and we'll work with you to make adjustments."
        },
        {
          question: "How do I contact customer support?",
          answer: "You can reach our support team via email at support@bandcam.io, phone at (555) 123-4567, or through the contact form on our website. We typically respond within 24 hours."
        }
      ]
    }
  ];

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  const filteredFAQs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Find answers to the most common questions about BandCam.io
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white text-black border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFAQs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-12">
              <h2 className="text-2xl font-bold mb-6 font-display text-goldie">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((item, itemIndex) => {
                  const globalIndex = categoryIndex * 100 + itemIndex;
                  const isOpen = openItems.includes(globalIndex);
                  
                  return (
                    <div key={itemIndex} className="bg-sky text-black rounded-lg border-2 border-black overflow-hidden">
                      <button
                        onClick={() => toggleItem(globalIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-goldie hover:bg-opacity-20 transition-colors"
                      >
                        <span className="font-semibold text-lg">{item.question}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-goldie" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-goldie" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          
          {filteredFAQs.length === 0 && searchQuery && (
            <div className="text-center py-12">
              <p className="text-xl text-gray-300 mb-4">
                No FAQs found matching "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-goldie hover:underline"
              >
                Clear search and show all FAQs
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-display">
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-goldie text-black hover:bg-opacity-90 px-8 py-4 border-2 border-black font-bold rounded-lg"
            >
              Contact Support
            </Link>
            <Link
              to="/help"
              className="bg-transparent text-black border-2 border-black hover:bg-black hover:text-white px-8 py-4 font-bold rounded-lg"
            >
              Visit Help Center
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
